//==============================================================================
// Test Locator Map
//
// Bing maps component, originally from the native Map module, updated to use
// test locator data.
//==============================================================================
/// <reference path='../../../../node_modules/@msdyn365-commerce-modules/map/src/modules/map/types/MicrosoftMaps/Microsoft.Maps.All.d.ts' />

import * as React from 'react';
import { observable, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { IAdministratorEntity } from '../../../actions/DataServiceEntities.g';

import { ITestLocatorProps, IPushpinOptionsData } from '../test-locator.props.autogenerated';
import { ITestLocatorData } from '../test-locator.data';

//==============================================================================
// INTERFACES
//==============================================================================
export interface ITestLocatorMapProps {
    cls: Function;
    moduleProps: ITestLocatorProps<ITestLocatorData>;
    admins: IAdministratorEntity[];
    selectedAdmin?: IAdministratorEntity;
    selectAdmin: Function;
}

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * TestLocatorMap component
 * @extends {React.Component<ITestLocatorMapProps>}
 */
//==============================================================================
@observer
class TestLocatorMap extends React.PureComponent<ITestLocatorMapProps> {
    //==========================================================================
    // VARIABLES
    //==========================================================================
    @observable public map: Microsoft.Maps.Map | undefined;
    private readonly mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    //------------------------------------------------------
    // Constructor
    //------------------------------------------------------
    public constructor(props: ITestLocatorMapProps) {
        super(props);
    }

    //------------------------------------------------------
    // Invoked immediately after component is mounted
    //------------------------------------------------------
    public async componentDidMount(): Promise<void> {
        const { context, data } = this.props.moduleProps;
        const { telemetry } = context;
        const { channel, locale } = context.actionContext.requestContext;
        const { storeSelectorStateManager } = data;

        if (channel && !channel.BingMapsApiKey) {
            telemetry.error('BingMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.BingMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }

        if (storeSelectorStateManager.result?.loadMapApi) {
            await storeSelectorStateManager.result.loadMapApi({
                key: channel?.BingMapsApiKey,
                lang: locale,
                market: channel?.ChannelCountryRegionISOCode
            });
        }

        if (storeSelectorStateManager.result?.isMapApiLoaded) {
            this._initMap();
            this._updateMap();
        }

        when(
            () => !!storeSelectorStateManager.result?.setMapModuleLoaded,
            () => {
                storeSelectorStateManager.result?.setMapModuleLoaded(true);
            }
        );

        reaction(
            () => storeSelectorStateManager.result?.loadMapApi,
            () => {
                storeSelectorStateManager.result?.loadMapApi({
                    key: channel?.BingMapsApiKey,
                    lang: locale,
                    market: channel?.ChannelCountryRegionISOCode
                });
            }
        );

        reaction(
            () => storeSelectorStateManager.result?.isMapApiLoaded,
            () => {
                this._initMap();
                this._updateMap();
            }
        );

        reaction(
            () => {
                return [
                    this.props.moduleProps.data.storeSelectorStateManager.result?.context?.orgUnitStoreInformation,
                    this.props.moduleProps.data.storeSelectorStateManager.result?.selectedStoreLocationId,
                    this.map
                ];
            },
            () => {
                this._updateMap();
            }
        );
    }

    //------------------------------------------------------
    // Render function
    //------------------------------------------------------
    public render(): JSX.Element {
        const { cls } = this.props;

        return (
            <div className={classnames('ms-map', cls('map'))}>
                <div className={classnames('ms-map__body', cls('map-body'))} ref={this.mapRef as React.RefObject<HTMLDivElement>} />
            </div>
        );
    }

    //==========================================================================
    // PRIVATE METHODS
    //==========================================================================
    //------------------------------------------------------
    // Initialize the map
    //------------------------------------------------------
    private readonly _initMap = () => {
        const { context, data } = this.props.moduleProps;
        const { channel } = context.actionContext.requestContext;
        const { storeSelectorStateManager } = data;

        if (storeSelectorStateManager.result?.isMapApiLoaded) {
            this.map = new Microsoft.Maps.Map(this.mapRef.current as HTMLElement, {
                credentials: channel?.BingMapsApiKey,
                pushpinAccessible: true
            });
            Microsoft.Maps.Events.addHandler(this.map, 'click', async () => {
                await storeSelectorStateManager.result?.setSelectedStoreLocationId(undefined);
            });
        }
    };

    //------------------------------------------------------
    // Update the map
    //------------------------------------------------------
    private readonly _updateMap = () => {
        const { moduleProps, admins, selectedAdmin } = this.props;

        if (this.map) {
            this.map.entities.clear();
            const pushpins: Microsoft.Maps.IPrimitive[] = [];

            admins.forEach((admin, index) => {
                if (admin.LocatorLatitude && admin.LocatorLongitude) {
                    const isSelectedAdmin = admin === selectedAdmin;
                    const pushpinOptions = moduleProps.config.pushpinOptions;
                    const options = this._getPushpinOptions(isSelectedAdmin, index, pushpinOptions);
                    const pushpin = new Microsoft.Maps.Pushpin(
                        new Microsoft.Maps.Location(admin.LocatorLatitude, admin.LocatorLongitude),
                        options
                    );

                    Microsoft.Maps.Events.addHandler(pushpin, 'click', () => {
                        admin && this.props.selectAdmin(admin);
                    });

                    isSelectedAdmin ? pushpins.unshift(pushpin) : pushpins.push(pushpin);
                }
            });

            if (selectedAdmin) {
                const currentLocation = new Microsoft.Maps.Location(selectedAdmin.LocatorLatitude, selectedAdmin.LocatorLongitude);
                this.map.setView({ center: currentLocation });
            } else {
                // Create a LocationRect from array of pushpins and set the custom-map view.
                this.map.setView({
                    bounds: Microsoft.Maps.LocationRect.fromShapes(pushpins)
                });
            }

            this.map.setOptions({ pushpinAccessible: true });
            this.map.entities.push(pushpins);
        }
    };

    //------------------------------------------------------
    // Get pushpin options
    //------------------------------------------------------
    private readonly _getPushpinOptions = (isSelected: boolean, index: number, pushpinOptions?: IPushpinOptionsData) => {
        const text = pushpinOptions?.showIndex ? (index + 1).toString() : undefined;
        const size = pushpinOptions?.size || 1;
        const color = isSelected ? pushpinOptions?.selectionColor || pushpinOptions?.color : pushpinOptions?.color;
        const icon = this._getSvgIcon(size, color, text);

        return {
            // Fallback if icon doesn't render properly
            color: isSelected ? pushpinOptions?.selectionColor : pushpinOptions?.color,
            icon
        };
    };

    //------------------------------------------------------
    // Get pushpin icon
    //------------------------------------------------------
    private readonly _getSvgIcon = (size: number, color?: string, text?: string): string => {
        const baseValue: number = 32;
        return (
            `<svg xmlns='http://www.w3.org/2000/svg' width='${baseValue * size}' height='${baseValue * size}' viewBox='0 0 365 560'>
                <path fill='${color}' d='M182.9,551.7c0,0.1,0.2,0.3,0.2,0.3S358.3,283,358.3,194.6c0-130.1-88.8-186.7-175.4-186.9 C96.3,7.9,7.5,64.5,7.5,194.6c0,88.4,175.3,357.4,175.3,357.4S182.9,551.7,182.9,551.7z' />
                ${text ? `<text x='185' y='280' style='font-size:220px;fill:#ffffff;' text-anchor='middle'>${text}</text>` : ''}
            </svg>`
        );
    };
}

export default TestLocatorMap;
